import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import type { TourStop } from 'fym-shared/src/music24/onboardingTour';
import { TypedMemo } from '../../common/types';
import { tourMessages as messages } from './tourMessages';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide bar; each part uses its own slice of it */
import styles from './TourGuideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StopCopy {
  title: MessageDescriptor;
  body: MessageDescriptor;
  /** The proof line of the narrow bar (b7). Only kit and home have one; the others keep the body. */
  short?: MessageDescriptor;
}

const STOP_COPY: Record<Exclude<TourStop, 'home'>, StopCopy> = {
  kit: { title: messages.kitTitle, body: messages.kitBody, short: messages.kitShort },
  plan: { title: messages.planTitle, body: messages.planBody },
  link: { title: messages.linkTitle, body: messages.linkBody },
  numbers: { title: messages.numbersTitle, body: messages.numbersBody },
};

export interface TourStopMessageProps {
  stop: TourStop;
  isMember: boolean;
  workspaceName: string;
  /** The id of the title: the tour card names its dialog with it. */
  titleId?: string;
}

/** The sentence and the proof line of one stop (design b1-b6), in the tour card. */
const TourStopMessageComponent: React.FC<TourStopMessageProps> = ({ stop, isMember, workspaceName, titleId }) => {
  const intl = useIntl();
  let eyebrow = intl.formatMessage(messages.eyebrow);
  let title: string;
  let body: string;
  let short: string | undefined;
  if (stop === 'home') {
    eyebrow = intl.formatMessage(messages.homeEyebrow);
    if (isMember) {
      title = intl.formatMessage(messages.memberTitle);
      body = intl.formatMessage(messages.memberBody, { workspace: workspaceName });
    } else {
      title = intl.formatMessage(messages.homeTitle);
      body = intl.formatMessage(messages.homeBody);
      short = intl.formatMessage(messages.homeShort);
    }
  } else {
    const copy = STOP_COPY[stop];
    title = intl.formatMessage(copy.title);
    body = intl.formatMessage(copy.body);
    short = copy.short === undefined ? undefined : intl.formatMessage(copy.short);
  }

  return (
    <div className={styles.message}>
      <span className={styles.eyebrow}>{eyebrow}</span>
      <b id={titleId} className={styles.title}>
        {title}
      </b>
      <small className={short === undefined ? styles.body : styles.bodyWide}>{withBrandWord(body)}</small>
      {short !== undefined && <small className={styles.bodyShort}>{withBrandWord(short)}</small>}
    </div>
  );
};

export const TourStopMessage = TypedMemo(TourStopMessageComponent);
